import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CreditCard, ExternalLink, Loader2, AlertCircle, Sparkles, Receipt } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { apiService } from '../../services/api';
import type { BillingHistoryItem } from '../../services/api';
import { trackUpgradeClick } from '../../utils/analytics';

/**
 * Страница биллинга: управление подпиской (Stripe Portal) и история платежей.
 */
const Billing: React.FC = () => {
  const { isAuthenticated, isPro, refreshUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [history, setHistory] = useState<BillingHistoryItem[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [portalError, setPortalError] = useState<string | null>(null);
  const checkoutStartedRef = useRef(false);

  // Обновить данные пользователя при заходе на Billing (один раз при isAuthenticated)
  useEffect(() => {
    if (isAuthenticated) refreshUser();
  }, [isAuthenticated, refreshUser]); // refreshUser стабилен (useCallback в AuthContext)

  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    apiService
      .getBillingHistory(50)
      .then((data) => {
        if (!cancelled) setHistory(data);
      })
      .catch(() => {
        if (!cancelled) setHistory([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingHistory(false);
      });
    return () => { cancelled = true; };
  }, [isAuthenticated]);

  const handleManageSubscription = async () => {
    setPortalError(null);
    setPortalLoading(true);
    try {
      const { url } = await apiService.createPortalSession();
      window.location.href = url;
      return;
    } catch (e: any) {
      setPortalError(e?.message || 'Failed to open billing portal.');
    } finally {
      setPortalLoading(false);
    }
  };

  const handleStartCheckout = async () => {
    trackUpgradeClick('billing_page', 'start_checkout');
    setPortalError(null);
    setCheckoutLoading(true);
    try {
      const { url } = await apiService.createCheckoutSession();
      window.location.href = url;
    } catch (e: any) {
      setPortalError(e?.message || 'Could not start checkout. Try again.');
    } finally {
      setCheckoutLoading(false);
    }
  };

  // После редиректа с Login с ?checkout=1 — один раз запускаем Checkout; при ошибке/таймауте убираем ?checkout=1 и показываем обычную страницу
  useEffect(() => {
    if (!isAuthenticated || isPro || checkoutStartedRef.current) return;
    if (searchParams.get('checkout') !== '1') return;
    checkoutStartedRef.current = true;
    let cancelled = false;
    const timeoutMs = 15000;
    const timeoutId = setTimeout(() => {
      if (cancelled) return;
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete('checkout');
        return next;
      }, { replace: true });
    }, timeoutMs);
    apiService
      .createCheckoutSession()
      .then(({ url }) => {
        if (!cancelled) window.location.href = url;
      })
      .catch(() => {
        if (!cancelled) {
          setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.delete('checkout');
            return next;
          }, { replace: true });
        }
      })
      .finally(() => {
        clearTimeout(timeoutId);
      });
    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [isAuthenticated, isPro, searchParams, setSearchParams]);

  const formatDate = (s: string) => {
    try {
      const d = new Date(s);
      return d.toLocaleDateString(undefined, { dateStyle: 'medium' });
    } catch {
      return s;
    }
  };

  const formatAmount = (cents: number, currency: string) => {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: (currency || 'usd').toUpperCase(),
    }).format(cents / 100);
  };

  if (!isAuthenticated) {
    return (
      <div className="content">
        <div className="container mx-auto px-4 py-12 max-w-xl">
          <div
            className="billing-auth-card rounded-2xl p-8 text-center border"
          >
            <Sparkles className="w-12 h-12 text-brand-gold mx-auto mb-4 opacity-90" />
            <h2 className="text-xl font-bold text-white mb-2">Billing & Subscription</h2>
            <p className="text-white/70 mb-6">Log in to manage your subscription and view payment history.</p>
            <Link
              to={'/login?redirect=' + encodeURIComponent('/billing?checkout=1')}
              className="btn btn-gold inline-flex items-center gap-2"
              onClick={() => trackUpgradeClick('billing_unauthenticated')}
            >
              Log in / Join
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (isAuthenticated && !isPro && searchParams.get('checkout') === '1') {
    return (
      <div className="content page-placeholder">
        <div className="container mx-auto px-4 py-12 max-w-xl text-center flex flex-col min-h-full justify-center">
          <div
            className="billing-checkout-redirect-card rounded-2xl p-8 border inline-block text-left"
          >
            <div className="flex justify-center mb-6">
              <Loader2 className="w-10 h-10 animate-spin text-brand-gold" />
            </div>
            <h2 className="text-xl font-bold text-white mb-2">Redirecting to checkout…</h2>
            <p className="text-white/70 text-sm">Please wait.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="content">
      <div className="container mx-auto px-4 py-8 pb-16 sm:pb-24 max-w-6xl">
        {/* Page header */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white mb-1">Billing & Subscription</h1>
            <p className="text-white/60 text-sm">Manage your plan and view invoices</p>
          </div>
          {isPro && (
            <span
              className="billing-premium-badge inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-bold text-black"
            >
              <Sparkles className="w-4 h-4" />
              Premium
            </span>
          )}
        </div>

        {/* Subscription card */}
        <div
          className={`billing-subscription-card rounded-2xl p-6 sm:p-8 mb-8 border ${isPro ? 'billing-subscription-card--pro' : 'billing-subscription-card--free'}`}
        >
          <div className="flex items-start gap-3 mb-4">
            <div
              className="billing-subscription-icon-wrap w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
            >
              <CreditCard className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">Subscription</h2>
              {isPro ? (
                <p className="text-white/70 text-sm mt-0.5">
                  You're on Premium. Update payment method, cancel, or view details in the secure portal.
                </p>
              ) : (
                <p className="text-white/70 text-sm mt-0.5">
                  Upgrade to Premium for real-time data and full features. Manage everything from the billing portal after you subscribe.
                </p>
              )}
            </div>
          </div>
          {portalError && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-400 mb-4 text-sm">
              <AlertCircle className="w-5 h-5 shrink-0" />
              <span>{portalError}</span>
            </div>
          )}
          <div className="flex flex-wrap gap-3 mt-4">
            <button
              type="button"
              onClick={handleManageSubscription}
              disabled={portalLoading || checkoutLoading}
              className="btn btn-outline flex items-center gap-2 disabled:opacity-50"
            >
              {portalLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <ExternalLink className="w-4 h-4" />
              )}
              {portalLoading ? 'Opening…' : 'Manage subscription'}
            </button>
            {!isPro && (
              <button
                type="button"
                onClick={handleStartCheckout}
                disabled={portalLoading || checkoutLoading}
                className="btn btn-gold inline-flex items-center gap-2 disabled:opacity-50"
              >
                {checkoutLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Redirecting…
                  </>
                ) : (
                  'Subscribe to Premium'
                )}
              </button>
            )}
          </div>
        </div>

        {/* Payment history — та же ширина, что и Subscription (card-glass по умолчанию max-width: 500px — сбрасываем) */}
        <div className="card-glass billing-history-card rounded-2xl overflow-hidden w-full mt-8">
          <div className="p-4 sm:p-6 border-b border-white/10 flex items-center gap-3">
            <Receipt className="w-5 h-5 text-brand-gold shrink-0" />
            <h2 className="text-lg font-semibold text-white">Payment history</h2>
          </div>
          <div className="p-4 sm:p-6">
            {loadingHistory ? (
              <div className="flex flex-col items-center justify-center py-16 gap-3">
                <Loader2 className="w-10 h-10 animate-spin text-brand-gold" />
                <span className="text-white/50 text-sm">Loading history…</span>
              </div>
            ) : history.length === 0 ? (
              <div className="text-center py-16">
                <Receipt className="w-12 h-12 text-white/20 mx-auto mb-3" />
                <p className="text-white/50">No payments yet</p>
                {!isPro && (
                  <Link to="/pricing" className="text-brand-gold font-semibold hover:underline mt-2 inline-block">
                    Subscribe to Premium
                  </Link>
                )}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="py-3 px-3 text-white/50 font-medium text-xs uppercase tracking-wider">Date</th>
                      <th className="py-3 px-3 text-white/50 font-medium text-xs uppercase tracking-wider">Amount</th>
                      <th className="py-3 px-3 text-white/50 font-medium text-xs uppercase tracking-wider hidden sm:table-cell">Period</th>
                      <th className="py-3 px-3 text-white/50 font-medium text-xs uppercase tracking-wider">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((item) => (
                      <tr key={item.id} className="border-b border-white/5 hover:bg-white/[0.02] transition">
                        <td className="py-3 px-3 text-white/90 text-sm">{formatDate(item.createdAt)}</td>
                        <td className="py-3 px-3 text-white font-medium">{formatAmount(item.amountCents, item.currency)}</td>
                        <td className="py-3 px-3 text-white/60 text-sm hidden sm:table-cell">
                          {formatDate(item.periodStart)} – {formatDate(item.periodEnd)}
                        </td>
                        <td className="py-3 px-3">
                          <span
                            className={`inline-flex px-2.5 py-1 rounded-md text-xs font-medium ${
                              item.status === 'succeeded'
                                ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                                : item.status === 'failed'
                                ? 'bg-red-500/20 text-red-400 border border-red-500/30'
                                : 'bg-white/10 text-white/70 border border-white/10'
                            }`}
                          >
                            {item.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Billing;
